// Full EN/FR/ES translation dictionary for PROJECT TITAN.
// Institutional register — reviewed for tone, register, and pan-European corporate usage.
export const translations = {
  en: {
    common: {
      exploreInfra: "Explore infrastructure",
      ourProjects: "Our projects",
      investorPortal: "Investor portal",
      learnMore: "Learn more",
      contactUs: "Contact us",
      getInTouch: "Get in touch",
      downloadPdf: "Download PDF",
      readMore: "Read more",
      viewAll: "View all",
      login: "Sign in",
      register: "Request access",
      logout: "Sign out",
      language: "Language",
      requestAccess: "Request access",
      backToHome: "Back to home",
      readAll: "Read the full report",
      applyNow: "Apply",
      openPosition: "Open position",
      seeAll: "See all",
    },
    nav: {
      home: "Home",
      company: "Company",
      about: "About",
      vision: "Vision",
      leadership: "Leadership",
      sustainability: "Sustainability",
      careers: "Careers",
      infrastructure: "Infrastructure",
      technology: "Technology",
      projects: "Projects",
      investors: "Investors",
      investorRelations: "Investor Relations",
      partners: "Partners",
      news: "News",
      faq: "FAQ",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Strategy Intelligence Solutions LTD — United Kingdom",
      title: "Building Europe's Next Generation AI Infrastructure.",
      subtitle: "The physical foundation of artificial intelligence — from photon to prompt. Data centers, GPU clusters, HPC, energy and fibre, operated at institutional grade.",
    },
    stats: {
      operational: "MW operational",
      pipeline: "MW in pipeline",
      sites: "European sites",
      pue: "average PUE",
    },
    marquee: "Trusted framework · ISO 27001 aligned · Uptime Institute · GDPR · SOC 2 in progress · CE certified · Tier III+ · CISPE",
    home: {
      pillarsEyebrow: "What we build",
      pillarsTitle: "Six vertically integrated capabilities.",
      pillarsIntro: "We design, finance, build and operate every layer of the AI stack — from the substation to the accelerator.",
      pillars: [
        { title: "AI Data Centers", desc: "Tier III+ campuses purpose-built for dense AI training workloads, engineered for 100 kW/rack and beyond." },
        { title: "GPU Clusters & HPC", desc: "NVIDIA H200, B200 and next-generation platforms deployed as reference architectures with day-one utilisation." },
        { title: "Liquid Cooling", desc: "Direct-to-chip and immersion cooling loops delivering sub-1.10 PUE with water-neutral operations." },
        { title: "Power Infrastructure", desc: "Grid interconnects, on-site substations and battery storage arrays sized for gigawatt trajectories." },
        { title: "Renewable Energy", desc: "PPAs with hydro, wind and solar producers across Europe — verifiable, additional, and time-matched." },
        { title: "Fibre & Cloud", desc: "Dark-fibre backbones and sovereign cloud regions serving regulated industries across the EU and UK." },
      ],
      projectsEyebrow: "Selected projects",
      projectsTitle: "A pan-European portfolio, engineered for scale.",
      investorEyebrow: "Investor Relations",
      investorTitle: "Institutional-grade transparency.",
      investorBody: "Comprehensive corporate reporting, governance disclosures and technical documentation — designed for professional counterparties.",
      ctaEyebrow: "Partner with us",
      ctaTitle: "Long-horizon capital deployed into physical AI infrastructure.",
      ctaBody: "We welcome dialogue with sovereign, pension, insurance and family-office capital partners aligned with a multi-decade infrastructure thesis.",
    },
    home2: {
      hero: {
        eyebrow: "Strategy Intelligence Solutions LTD — United Kingdom",
        title: "Invest in the infrastructure powering the AI revolution.",
        subtitle: "Access professionally managed AI infrastructure investments from $10,000. Own a stake in Europe's next generation of data centers, GPU clusters and renewable-powered compute.",
        ctaPrimary: "Become an investor",
        ctaSecondary: "Learn more",
        disclaimer: "Reserved for eligible private investors · Not a public offer · Subject to eligibility",
      },
      trust: [
        { k: "Minimum", v: "From $10,000" },
        { k: "Management", v: "Professionally managed" },
        { k: "Sector", v: "AI infrastructure" },
        { k: "Reporting", v: "Fully transparent" },
        { k: "Structure", v: "Institutional-grade" },
      ],
      what: {
        eyebrow: "What is Titan?",
        title: "A simple way to own a piece of Europe's AI backbone.",
        body: "Titan builds and operates the physical infrastructure that trains and runs artificial intelligence — and lets qualifying private investors participate.",
      },
      whatItems: [
        { t: "Real assets", d: "Data centers, GPU clusters, fibre networks and renewable power — assets you can locate on a map." },
        { t: "Long-term growth", d: "Investments structured for a multi-year AI infrastructure build-out. No short-term speculation." },
        { t: "Managed for you", d: "A dedicated operations and investor relations team handle everything, from technology to reporting." },
      ],
      why: {
        eyebrow: "Why now?",
        title: "AI is compute-hungry. Europe is under-built.",
      },
      whyItems: [
        { stat: "+300%", label: "AI compute demand by 2030", body: "Global AI training compute is doubling every 6 months. Europe faces a structural infrastructure gap." },
        { stat: "€200B+", label: "European build-out to 2030", body: "Independent estimates place required investment in European AI data centers above €200 billion this decade." },
        { stat: "≤ 5%", label: "European market share today", body: "Europe hosts less than 5 % of global AI training capacity — a gap independent operators like Titan are built to close." },
      ],
      steps: {
        eyebrow: "How it works",
        title: "Four steps to your first investment.",
        body: "The whole journey takes under 30 minutes online. Our team is available at every step.",
      },
      steps_list: [
        { stepLabel: "Step 01", title: "Create your account", body: "Register with your email and set up secure access to your private investor portal.", time: "2 minutes" },
        { stepLabel: "Step 02", title: "Verify your identity", body: "Upload a passport or ID card and a proof of address. Our compliance team reviews within 24 hours.", time: "5 minutes" },
        { stepLabel: "Step 03", title: "Fund your investment", body: "Choose the amount ($10,000+) and fund by bank transfer. You will receive full documentation.", time: "10 minutes" },
        { stepLabel: "Step 04", title: "Receive ongoing reporting", body: "Quarterly performance updates, project reports and access to the secure Data Room.", time: "Ongoing" },
      ],
      human: {
        eyebrow: "The team",
        title: "Real people. Real infrastructure. Real accountability.",
        body: "We are engineers, operators and investors who have built and run mission-critical infrastructure for two decades. When you invest with Titan, you know who you are trusting.",
        cta: "Meet the leadership",
        badgeEyebrow: "Headquartered",
        badgeText: "London, United Kingdom — with operations across Sweden, Spain and the Netherlands.",
      },
      infra: {
        eyebrow: "Where your capital works",
        title: "Every megawatt is engineered, monitored and reported to institutional standards.",
      },
      finalCta: {
        title: "Ready to invest in the AI infrastructure of tomorrow?",
        body: "Open an account in minutes or schedule a call with our investor relations team to discuss your goals.",
        next: "Your next step",
        scheduleCall: "Schedule a call",
        download: "Read the Investor Overview",
        disclaimer: "Investments involve risk. Past performance is not indicative of future results. Not a public offer.",
      },
      cases: {
        eyebrow: "Investor stories",
        title: "How our members put their capital to work.",
        body: "Selected experiences from private and professional investors — shared with their permission and anonymised on request.",
        items: [
          { name: "M.C.", role: "Family Office Principal", location: "London, UK", allocation: "$85,000", tenure: "18 months",
            quote: "The level of transparency is uncommon at this ticket size. Quarterly updates read like institutional reports and the Data Room is genuinely useful. I know exactly where my capital is deployed." },
          { name: "P.K.", role: "Technology Executive", location: "Zurich, CH", allocation: "$25,000", tenure: "9 months",
            quote: "I wanted exposure to the AI build-out without picking stocks. Titan let me back the actual megawatts. The onboarding was straightforward and the KYC review was faster than my bank." },
          { name: "S.R.", role: "Private Investor", location: "Amsterdam, NL", allocation: "$40,000", tenure: "12 months",
            quote: "Real infrastructure, real reporting, real people on the phone when I have questions. It feels closer to a private-markets platform than to anything I've used before." },
        ],
        note: "Testimonials shared voluntarily. Not indicative of future performance. Investor names and photographs anonymised at each investor's request.",
      },
    },
    risk: {
      eyebrow: "Risk warning",
      pageTitle: "Investment risk warning.",
      headline: "Don't invest unless you're prepared to lose all the money you invest.",
      body: "This is a high-risk investment. You are unlikely to be protected by any regulator or compensation scheme if something goes wrong. Take 2 minutes to learn more.",
      readMore: "Read the full risk warning →",
      keyRisksTitle: "Key risks you should understand",
      bullets: [
        { t: "You could lose all your money", d: "Investments in infrastructure and private assets can go down as well as up. In a worst-case scenario, you could lose 100 % of the money you invest." },
        { t: "Illiquidity", d: "You will likely not be able to sell your investment when you want to. There is no established secondary market and redemptions may be restricted." },
        { t: "Lack of protection", d: "The UK Financial Services Compensation Scheme (FSCS) does not typically cover this type of investment. You are unlikely to have recourse to the Financial Ombudsman Service." },
        { t: "No dividends or return guarantees", d: "Any projections are illustrative only and do not guarantee future returns. Cash distributions, if any, depend on operational performance." },
        { t: "Concentration and sector risk", d: "The AI-infrastructure sector is capital-intensive, exposed to power costs, permitting, supply chain and technological obsolescence risks." },
        { t: "Currency, tax and legal", d: "Amounts are quoted in USD but underlying assets may be denominated in EUR/GBP/other. Tax treatment depends on your circumstances and may change. Seek independent advice." },
      ],
      statutory1: "This financial promotion is issued by Strategy Intelligence Solutions LTD, a company registered in England & Wales. It is directed only at investors who qualify as (a) certified high-net-worth investors, (b) certified sophisticated investors, (c) self-certified sophisticated investors or (d) certified restricted investors under the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005 (as amended), and at professional clients / eligible counterparties within the meaning of the FCA rules.",
      statutory2: "Nothing on this website constitutes an offer, solicitation or recommendation to buy or subscribe for any security, and no reliance should be placed on it for the purpose of any investment decision. Recipients should not treat the contents as advice relating to legal, taxation or investment matters and are recommended to consult their own professional advisers.",
      statutory3: "By proceeding you confirm that you meet one of the categories above, that you have read this risk warning in full, and that you understand that the value of any investment can go down as well as up.",
    },
    schedule: {
      eyebrow: "Schedule a call",
      title: "Talk to our investor relations team.",
      body: "A 30-minute conversation to walk through the opportunity, answer your questions and — if appropriate — discuss next steps. No pressure, no obligation.",
      duration: "30 minutes · Video or phone",
      callbackNote: "Prefer a callback? Reply to your confirmation email with a preferred time.",
      disclaimer: "This call is not investment advice. Any information shared is for illustrative purposes only.",
      embedHint: "Calendar not loading?",
      openExternal: "Open in a new window",
    },
    about: {
      eyebrow: "About",
      title: "A UK-domiciled operator of European AI infrastructure.",
      body: "Strategy Intelligence Solutions LTD was formed to consolidate the fragmented supply of AI-ready infrastructure across Europe. We take a Brookfield-style operational approach — deep vertical integration, disciplined underwriting, and multi-decade holding horizons.",
      missionTitle: "Mission",
      missionBody: "Build, acquire and operate next-generation AI infrastructure across Europe.",
      visionTitle: "Vision",
      visionBody: "Become one of Europe's leading independent AI infrastructure companies within the decade.",
      valuesTitle: "Values",
      values: [
        { t: "Engineering excellence", d: "Every megawatt is designed, not procured." },
        { t: "Institutional discipline", d: "Governance, transparency and risk before growth." },
        { t: "Environmental stewardship", d: "Additionality-first renewable strategy." },
        { t: "Long horizon", d: "Multi-decade infrastructure thinking." },
      ],
      timelineTitle: "Selected milestones",
    },
    vision: {
      eyebrow: "Vision",
      title: "The compute layer of the 21st-century economy.",
      body: "Artificial intelligence is becoming the primary industrial input of the next fifty years. The infrastructure that supports it — power, land, water, silicon and glass — is a strategic asset class. We are building it, in Europe, for European counterparties.",
      pillarsTitle: "Three horizons",
      pillars: [
        { code: "H1", title: "2024 – 2027", body: "Establish operational scale across five flagship sites. Consolidate a European fibre and power footprint. Achieve institutional-grade governance certifications." },
        { code: "H2", title: "2027 – 2032", body: "Grow to gigawatt-scale portfolio. Extend into sovereign cloud regions. Vertically integrate into on-site renewable generation and long-duration storage." },
        { code: "H3", title: "2032 – 2040", body: "Operate one of Europe's largest independent compute platforms. Deliver structural energy circularity across sites. Enable a generation of European AI companies." },
      ],
      thesisTitle: "Investment thesis",
      thesisBody: "The AI transition is compute-constrained, energy-constrained and geographically-constrained. Europe cannot rely on foreign sovereign compute. Independent, well-capitalised, engineering-led operators are essential.",
    },
    leadership: {
      eyebrow: "Leadership",
      title: "Operators, engineers, allocators.",
      body: "A leadership team combining hyperscale operational experience, institutional capital discipline, and deep engineering fluency.",
      board: "Board of directors",
      execTeam: "Executive team",
      advisors: "Advisors",
      members: [
        { name: "A. Whitmore", role: "Chief Executive Officer", bio: "Twenty years scaling hyperscale colocation and network infrastructure across EMEA.", area: "exec" },
        { name: "M. Laurent", role: "Chief Financial Officer", bio: "Former infrastructure principal at a European allocator. CFA charterholder.", area: "exec" },
        { name: "R. Nováková", role: "Chief Operating Officer", bio: "Two decades in mission-critical facility operations. Uptime Institute AOS.", area: "exec" },
        { name: "K. Fernández", role: "Chief Technology Officer", bio: "Former principal engineer at a hyperscale AI training platform.", area: "exec" },
        { name: "S. Al-Rashid", role: "Chief Sustainability Officer", bio: "Energy-transition specialist, previously at a European utility.", area: "exec" },
        { name: "D. Bergström", role: "Independent Chair", bio: "Chair of audit at two FTSE 250 infrastructure operators.", area: "board" },
        { name: "H. Okafor", role: "Non-executive Director", bio: "Former deputy governor, monetary authority. Institutional risk expertise.", area: "board" },
        { name: "L. Costa", role: "Non-executive Director", bio: "Managing partner at a European pension consultancy.", area: "board" },
      ],
    },
    sustainability: {
      eyebrow: "Sustainability",
      title: "Additionality-first, not accounting-first.",
      body: "Our environmental strategy is grounded in time-matched, geographically-matched and additional renewable procurement. We do not settle for annualised averages or unbundled certificates.",
      pillarsTitle: "Framework",
      pillars: [
        { t: "24/7 Carbon-free energy", d: "Hourly time-matched renewable procurement across all operational sites by 2027." },
        { t: "Water-positive operations", d: "Closed-loop cooling architectures. Zero potable water use in new-build campuses." },
        { t: "Circular hardware", d: "Server, GPU and cable refurbishment partnerships. Component-level traceability." },
        { t: "Community integration", d: "Waste-heat redistribution to district heating networks where geography permits." },
      ],
      metricsTitle: "2026 targets",
      metrics: [
        { k: "PUE ceiling", v: "1.15" },
        { k: "Renewable coverage", v: "100 %" },
        { k: "Water usage effectiveness", v: "0.10 L/kWh" },
        { k: "Scope 1+2 net", v: "0 tCO₂e" },
      ],
      reportsTitle: "Reports & disclosures",
      reportsBody: "Annual ESG report, CDP submission and TCFD-aligned climate risk disclosures available in the document center.",
    },
    news: {
      eyebrow: "Newsroom",
      title: "Corporate updates & press releases.",
      body: "Operational milestones, financial announcements, corporate governance updates and press coverage.",
      readingTime: "min read",
      items: [
        { date: "2026-02-14", category: "Operational", title: "Nordic Data Lab reaches 42 MW commissioned capacity.", body: "The Stockholm campus has achieved full commissioning of its first phase, delivering 42 MW of AI-ready capacity with a measured PUE of 1.08." },
        { date: "2026-01-30", category: "Corporate", title: "Strategy Intelligence Solutions confirms 220 MW pipeline for 2027.", body: "The company has confirmed its European build pipeline at 220 MW, spread across sites in Spain, the United Kingdom and the Netherlands." },
        { date: "2026-01-12", category: "Governance", title: "Independent chair appointed to the board.", body: "The board welcomes an independent chair with two decades of FTSE 250 audit committee experience." },
        { date: "2025-12-05", category: "Sustainability", title: "24/7 CFE procurement agreement signed for Nordic operations.", body: "A multi-year time-matched renewable procurement contract has been finalised with a Nordic hydro producer." },
        { date: "2025-11-18", category: "Technology", title: "First B200 NVL72 pods staged for Iberia AI Campus.", body: "The first NVIDIA B200 reference systems have been staged for integration into the Iberia AI Campus in Madrid." },
      ],
    },
    careers: {
      eyebrow: "Careers",
      title: "Build the infrastructure of the next fifty years.",
      body: "We are hiring engineers, operators and allocators across the United Kingdom, Sweden, Spain and the Netherlands.",
      why: "Why join",
      whyItems: [
        { t: "Real infrastructure", d: "You will build megawatt-scale, mission-critical systems — not slide decks." },
        { t: "Long horizons", d: "We plan on multi-decade cycles. Careers can span an entire build-out." },
        { t: "Institutional discipline", d: "Rigorous governance, transparent decision-making, adult culture." },
        { t: "Ownership", d: "Meaningful equity across engineering, operations and leadership roles." },
      ],
      openRoles: "Open positions",
      roles: [
        { title: "Principal Data Center Engineer", location: "London, UK", type: "Full-time" },
        { title: "Head of Power Infrastructure", location: "Stockholm, SE", type: "Full-time" },
        { title: "Senior HPC / GPU Cluster Engineer", location: "Madrid, ES", type: "Full-time" },
        { title: "Sustainability & CFE Analyst", location: "Amsterdam, NL", type: "Full-time" },
        { title: "Investor Relations Associate", location: "London, UK", type: "Full-time" },
        { title: "Site Reliability Engineer, AI Platforms", location: "Aberdeen, UK", type: "Full-time" },
      ],
      contact: "General applications",
      contactEmail: "careers@strategyintelligence.co.uk",
    },
    partners: {
      eyebrow: "Partners",
      title: "A curated network of institutional partners.",
      body: "We work with global infrastructure counterparties — from silicon vendors and utilities to sovereign wealth capital and independent engineering firms.",
      categories: [
        { title: "Silicon & platform", items: ["NVIDIA reference partner (H200, B200)", "AMD Instinct alignment", "Broadcom Tomahawk fabric", "Micron high-bandwidth memory"] },
        { title: "Energy & utilities", items: ["Nordic hydro producers (PPA counterparties)", "UK offshore-wind operators", "Iberian solar independents", "European battery-storage integrators"] },
        { title: "Engineering & construction", items: ["Tier-1 European mechanical contractors", "Modular data-center system integrators", "Specialist liquid-cooling OEMs", "Fibre-splicing and OSP partners"] },
        { title: "Capital & advisors", items: ["Big-four audit relationship", "Magic-circle legal counsel", "Institutional infrastructure consultants", "Independent risk advisors"] },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently asked questions.",
      body: "The most common questions from institutional counterparties, journalists and prospective employees.",
      items: [
        {
          q: "What does Strategy Intelligence Solutions actually build?",
          a: "We design, finance, build and operate the physical infrastructure required to train and serve modern artificial intelligence workloads. This spans purpose-built AI data centres, GPU clusters, high-performance computing platforms, liquid-cooling systems, on-site power infrastructure, renewable procurement, dark-fibre backbones and sovereign cloud regions.",
        },
        {
          q: "Where does the company operate?",
          a: "We are UK-domiciled and operate assets across Sweden, Spain, the United Kingdom and the Netherlands, with a pan-European build pipeline of 220 MW targeted for delivery by 2027.",
        },
        {
          q: "Do you offer investment products to retail investors?",
          a: "Strategy Intelligence Solutions LTD is open to eligible private investors from $10,000, alongside institutional counterparties. We do not run a public offering. Access is granted after a compliance review, including identity verification and, where applicable, professional-investor classification under local regulation. Prospective investors should consult their own advisors regarding suitability.",
        },
        {
          q: "How do you approach sustainability?",
          a: "We operate an additionality-first renewable strategy, with time-matched and geographically-matched power procurement. Our target is 24/7 carbon-free energy across all operational sites by 2027 and a water-positive posture in new-build campuses.",
        },
        {
          q: "Which counterparties do you work with?",
          a: "Silicon and platform partners include NVIDIA, AMD, Broadcom and Micron. On the energy side, we work with Nordic hydro producers, UK offshore-wind operators and Iberian solar independents. Our advisory bench includes big-four audit, magic-circle legal counsel and independent infrastructure consultants.",
        },
        {
          q: "How is the company governed?",
          a: "The company operates under an independent-chaired board with dedicated audit and risk committees. Reporting cadence is quarterly for operational metrics and semi-annual for financial disclosures, with external audit performed by a big-four firm.",
        },
        {
          q: "How can I request access to the investor portal?",
          a: "Institutional counterparties can request access via the portal registration page. Access is granted following a compliance review, including identity verification and, where applicable, professional-investor classification.",
        },
        {
          q: "Is my data secure?",
          a: "The platform is operated in alignment with ISO 27001 controls, is GDPR-compliant and is undergoing SOC 2 attestation. All personally identifiable information is encrypted at rest and in transit, and access is subject to full audit logging.",
        },
      ],
    },
    infra: {
      eyebrow: "Infrastructure",
      title: "The physical foundation of European AI.",
      body: "Eight vertically integrated capabilities, one operating model.",
    },
    tech: {
      eyebrow: "Technology",
      title: "Reference architectures, from photon to prompt.",
      body: "Cooling, compute, networking and power — engineered as one system.",
    },
    projects: {
      eyebrow: "Projects",
      title: "Selected pan-European infrastructure programmes.",
      location: "Location",
      power: "Power capacity",
      status: "Status",
      timeline: "Timeline",
      progress: "Progress",
      specs: "Technical specifications",
    },
    ir: {
      eyebrow: "Investor Relations",
      title: "Institutional-grade transparency.",
      body: "Corporate overview, governance, financial publications and technical documentation.",
      disclaimer: "The information on this site is provided for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any security. Nothing herein promises returns.",
      docs: "Document center",
      contactIR: "Contact Investor Relations",
    },
    contact: {
      eyebrow: "Contact",
      title: "Speak with our team.",
      body: "For institutional enquiries, partnerships and press.",
      name: "Full name",
      email: "Professional email",
      company: "Organisation",
      phone: "Phone (optional)",
      subject: "Subject",
      message: "Message",
      submit: "Send message",
      success: "Thank you — we will respond within one business day.",
      error: "The message could not be sent. Please try again.",
    },
    auth: {
      loginTitle: "Investor Portal",
      loginSubtitle: "Sign in to your institutional account.",
      registerTitle: "Request portal access",
      registerSubtitle: "Complete the form below to open an institutional account.",
      email: "Email",
      password: "Password",
      fullName: "Full name",
      companyField: "Organisation",
      countryField: "Country",
      submit: "Sign in",
      submitRegister: "Create account",
      noAccount: "No access yet?",
      haveAccount: "Already have access?",
      invalid: "Invalid credentials.",
    },
    portal: {
      welcome: "Welcome back",
      dashboard: "Dashboard",
      portfolio: "Portfolio",
      documents: "Documents",
      kyc: "Identity verification",
      withdrawals: "Withdrawal requests",
      messages: "Messages",
      profile: "Profile",
      overview: "Overview",
      committed: "Total committed",
      currentValue: "Current value",
      unrealized: "Unrealised",
      allocation: "Allocation",
      performance: "Portfolio performance",
      kycPending: "Verification pending",
      kycReview: "Under review",
      kycApproved: "Verified",
      kycRejected: "Verification rejected",
      kycStart: "Start verification",
      newWithdrawal: "Request a withdrawal",
      amount: "Amount",
      currency: "Currency",
      bank: "Bank name",
      iban: "IBAN",
      reason: "Reason (optional)",
      submit: "Submit request",
      noWithdrawals: "No withdrawal requests yet.",
      unread: "unread",
    },
    admin: {
      title: "Administration",
      overview: "Overview",
      investors: "Investors",
      projectsMgmt: "Projects",
      docsMgmt: "Documents",
      audit: "Audit logs",
      contactMsgs: "Contact messages",
      approve: "Approve",
      reject: "Reject",
      pending: "pending",
      kycReview: "Under review",
    },
    ai: {
      title: "Athena — AI Assistant",
      subtitle: "Ask about our infrastructure, technology or projects.",
      placeholder: "Ask Athena…",
      hint: "Powered by Strategy Intelligence Solutions",
      requiresAuth: "Sign in to chat with Athena.",
    },
    footer: {
      tagline: "Institutional infrastructure for the age of AI.",
      hq: "Registered office · United Kingdom",
      legal: "Legal notice",
      privacy: "Privacy",
      cookies: "Cookies",
      terms: "Terms",
      copyright: "Strategy Intelligence Solutions LTD. All rights reserved.",
    },
  },

  fr: {
    common: {
      exploreInfra: "Explorer l'infrastructure",
      ourProjects: "Nos projets",
      investorPortal: "Portail investisseur",
      learnMore: "En savoir plus",
      contactUs: "Nous contacter",
      getInTouch: "Prendre contact",
      downloadPdf: "Télécharger le PDF",
      readMore: "Lire la suite",
      viewAll: "Tout consulter",
      login: "Se connecter",
      register: "Demander l'accès",
      logout: "Se déconnecter",
      language: "Langue",
      requestAccess: "Demander l'accès",
      backToHome: "Retour à l'accueil",
      readAll: "Lire le rapport intégral",
      applyNow: "Postuler",
      openPosition: "Poste ouvert",
      seeAll: "Voir tout",
    },
    nav: {
      home: "Accueil",
      company: "Société",
      about: "Présentation",
      vision: "Vision",
      leadership: "Direction",
      sustainability: "Durabilité",
      careers: "Carrières",
      infrastructure: "Infrastructure",
      technology: "Technologie",
      projects: "Projets",
      investors: "Investisseurs",
      investorRelations: "Relations investisseurs",
      partners: "Partenaires",
      news: "Actualités",
      faq: "FAQ",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Strategy Intelligence Solutions LTD — Royaume-Uni",
      title: "Bâtir l'infrastructure d'IA de nouvelle génération en Europe.",
      subtitle: "La fondation physique de l'intelligence artificielle — du photon à l'inférence. Data centers, clusters GPU, HPC, énergie et fibre, exploités aux standards institutionnels.",
    },
    stats: {
      operational: "MW en exploitation",
      pipeline: "MW en pipeline",
      sites: "sites européens",
      pue: "PUE moyen",
    },
    marquee: "Cadre de confiance · Aligné ISO 27001 · Uptime Institute · RGPD · SOC 2 en cours · Certifié CE · Tier III+ · CISPE",
    home: {
      pillarsEyebrow: "Ce que nous bâtissons",
      pillarsTitle: "Six capacités verticalement intégrées.",
      pillarsIntro: "Nous concevons, finançons, construisons et exploitons chaque couche de la pile IA — de la sous-station à l'accélérateur.",
      pillars: [
        { title: "Data centers IA", desc: "Campus Tier III+ conçus pour les charges d'entraînement denses, dimensionnés pour 100 kW/rack et au-delà." },
        { title: "Clusters GPU & HPC", desc: "Plateformes NVIDIA H200, B200 et nouvelle génération déployées comme architectures de référence, opérationnelles dès le premier jour." },
        { title: "Refroidissement liquide", desc: "Boucles direct-to-chip et immersion délivrant un PUE inférieur à 1,10 avec des opérations neutres en eau." },
        { title: "Infrastructure électrique", desc: "Raccordements réseau, sous-stations sur site et systèmes de stockage batterie dimensionnés pour des trajectoires gigawatt." },
        { title: "Énergies renouvelables", desc: "PPA avec producteurs hydroélectriques, éoliens et solaires à travers l'Europe — vérifiables, additionnels et synchronisés temporellement." },
        { title: "Fibre & cloud", desc: "Backbones de fibre noire et régions cloud souveraines au service des industries régulées en UE et au Royaume-Uni." },
      ],
      projectsEyebrow: "Projets sélectionnés",
      projectsTitle: "Un portefeuille paneuropéen, conçu pour l'échelle.",
      investorEyebrow: "Relations investisseurs",
      investorTitle: "Transparence de niveau institutionnel.",
      investorBody: "Reporting corporate complet, publications de gouvernance et documentation technique — conçus pour des contreparties professionnelles.",
      ctaEyebrow: "Nous rejoindre",
      ctaTitle: "Capital long terme déployé dans l'infrastructure physique de l'IA.",
      ctaBody: "Nous accueillons le dialogue avec les partenaires en capital souverain, fonds de pension, assureurs et family offices alignés sur une thèse d'infrastructure pluridécennale.",
    },
    home2: {
      hero: {
        eyebrow: "Strategy Intelligence Solutions LTD — Royaume-Uni",
        title: "Investissez dans l'infrastructure qui alimente la révolution de l'IA.",
        subtitle: "Accédez à des investissements en infrastructure IA gérés par des professionnels, à partir de 10 000 $. Détenez une part des data centers, clusters GPU et énergies renouvelables qui construisent la prochaine génération européenne du calcul.",
        ctaPrimary: "Devenir investisseur",
        ctaSecondary: "En savoir plus",
        disclaimer: "Réservé aux investisseurs privés éligibles · Ne constitue pas une offre au public · Sous réserve d'éligibilité",
      },
      trust: [
        { k: "Minimum", v: "À partir de 10 000 $" },
        { k: "Gestion", v: "Gérée par des professionnels" },
        { k: "Secteur", v: "Infrastructure IA" },
        { k: "Reporting", v: "Totalement transparent" },
        { k: "Structure", v: "Niveau institutionnel" },
      ],
      what: {
        eyebrow: "Qu'est-ce que Titan ?",
        title: "Une manière simple de détenir une part de l'infrastructure IA européenne.",
        body: "Titan construit et exploite l'infrastructure physique qui entraîne et fait tourner l'intelligence artificielle — et permet aux investisseurs privés éligibles d'y participer.",
      },
      whatItems: [
        { t: "Actifs réels", d: "Data centers, clusters GPU, réseaux de fibre et énergie renouvelable — des actifs que vous pouvez localiser sur une carte." },
        { t: "Croissance long terme", d: "Investissements structurés pour un cycle pluriannuel de construction d'infrastructure IA. Aucune spéculation court terme." },
        { t: "Géré pour vous", d: "Une équipe dédiée aux opérations et aux relations investisseurs prend tout en charge, de la technologie au reporting." },
      ],
      why: {
        eyebrow: "Pourquoi maintenant ?",
        title: "L'IA a soif de calcul. L'Europe est sous-équipée.",
      },
      whyItems: [
        { stat: "+300 %", label: "Demande de calcul IA d'ici 2030", body: "La puissance de calcul mondiale pour l'entraînement IA double tous les 6 mois. L'Europe fait face à un déficit structurel." },
        { stat: "200 Md€+", label: "Investissement européen d'ici 2030", body: "Les estimations indépendantes évaluent l'investissement nécessaire dans les data centers IA européens à plus de 200 milliards d'euros cette décennie." },
        { stat: "≤ 5 %", label: "Part de marché européenne aujourd'hui", body: "L'Europe héberge moins de 5 % de la capacité d'entraînement IA mondiale — un écart que des opérateurs indépendants comme Titan sont conçus pour combler." },
      ],
      steps: {
        eyebrow: "Comment ça marche",
        title: "Quatre étapes vers votre premier investissement.",
        body: "L'ensemble du parcours prend moins de 30 minutes en ligne. Notre équipe est disponible à chaque étape.",
      },
      steps_list: [
        { stepLabel: "Étape 01", title: "Créez votre compte", body: "Inscrivez-vous avec votre email et configurez un accès sécurisé à votre portail investisseur privé.", time: "2 minutes" },
        { stepLabel: "Étape 02", title: "Vérifiez votre identité", body: "Téléchargez un passeport ou une pièce d'identité et un justificatif de domicile. Notre équipe conformité examine sous 24 heures.", time: "5 minutes" },
        { stepLabel: "Étape 03", title: "Financez votre investissement", body: "Choisissez le montant (10 000 $ ou plus) et financez par virement bancaire. Vous recevrez la documentation complète.", time: "10 minutes" },
        { stepLabel: "Étape 04", title: "Recevez un reporting continu", body: "Mises à jour trimestrielles de performance, rapports de projet et accès à la Data Room sécurisée.", time: "En continu" },
      ],
      human: {
        eyebrow: "L'équipe",
        title: "De vraies personnes. De vraies infrastructures. Une vraie responsabilité.",
        body: "Nous sommes des ingénieurs, opérateurs et investisseurs qui construisons et exploitons des infrastructures critiques depuis deux décennies. En investissant chez Titan, vous savez à qui vous faites confiance.",
        cta: "Rencontrer la direction",
        badgeEyebrow: "Siège",
        badgeText: "Londres, Royaume-Uni — avec des opérations en Suède, Espagne et Pays-Bas.",
      },
      infra: {
        eyebrow: "Là où votre capital travaille",
        title: "Chaque mégawatt est conçu, supervisé et reporté aux standards institutionnels.",
      },
      finalCta: {
        title: "Prêt à investir dans l'infrastructure IA de demain ?",
        body: "Ouvrez un compte en quelques minutes ou planifiez un appel avec notre équipe relations investisseurs pour discuter de vos objectifs.",
        next: "Votre prochaine étape",
        scheduleCall: "Planifier un appel",
        download: "Lire la présentation investisseur",
        disclaimer: "Tout investissement comporte des risques. Les performances passées ne préjugent pas des résultats futurs. Ne constitue pas une offre au public.",
      },
      cases: {
        eyebrow: "Retours d'investisseurs",
        title: "Comment nos membres mettent leur capital au travail.",
        body: "Expériences sélectionnées d'investisseurs privés et professionnels — partagées avec leur autorisation et anonymisées sur demande.",
        items: [
          { name: "M.C.", role: "Principal family office", location: "Londres, UK", allocation: "85 000 $", tenure: "18 mois",
            quote: "Le niveau de transparence est rare à ce ticket. Les mises à jour trimestrielles ressemblent à des rapports institutionnels et la Data Room est réellement utile. Je sais exactement où mon capital est déployé." },
          { name: "P.K.", role: "Cadre dirigeant tech", location: "Zurich, CH", allocation: "25 000 $", tenure: "9 mois",
            quote: "Je voulais m'exposer au build-out IA sans stock picking. Titan m'a permis de soutenir les vrais mégawatts. L'onboarding a été simple et le KYC plus rapide que celui de ma banque." },
          { name: "S.R.", role: "Investisseur privé", location: "Amsterdam, NL", allocation: "40 000 $", tenure: "12 mois",
            quote: "De vraies infrastructures, un vrai reporting, de vraies personnes au téléphone. C'est plus proche d'une plateforme de marchés privés que de tout ce que j'ai utilisé auparavant." },
        ],
        note: "Témoignages partagés volontairement. Non indicatifs de performances futures. Noms et photos anonymisés à la demande de chaque investisseur.",
      },
    },
    risk: {
      eyebrow: "Avertissement sur les risques",
      pageTitle: "Avertissement sur les risques d'investissement.",
      headline: "N'investissez pas à moins d'être prêt à perdre l'intégralité des sommes investies.",
      body: "Ceci est un investissement à haut risque. Vous êtes peu susceptible d'être protégé par un régulateur ou un fonds de garantie si quelque chose tourne mal. Prenez 2 minutes pour en savoir plus.",
      readMore: "Lire l'avertissement complet →",
      keyRisksTitle: "Principaux risques à comprendre",
      bullets: [
        { t: "Perte totale possible", d: "Les investissements dans l'infrastructure et les actifs privés peuvent baisser comme monter. Dans le pire des cas, vous pouvez perdre 100 % des sommes investies." },
        { t: "Illiquidité", d: "Vous ne pourrez probablement pas vendre votre investissement quand vous le souhaitez. Aucun marché secondaire établi et les rachats peuvent être restreints." },
        { t: "Absence de protection", d: "Le UK Financial Services Compensation Scheme (FSCS) ne couvre généralement pas ce type d'investissement. Vous n'aurez probablement pas recours au Financial Ombudsman Service." },
        { t: "Aucun dividende ni garantie de rendement", d: "Toute projection est illustrative uniquement et ne garantit pas les rendements futurs. Les distributions éventuelles dépendent de la performance opérationnelle." },
        { t: "Concentration et risque sectoriel", d: "Le secteur de l'infrastructure IA est capital-intensif, exposé aux coûts de l'énergie, aux autorisations, à la chaîne d'approvisionnement et à l'obsolescence technologique." },
        { t: "Devise, fiscalité et juridique", d: "Les montants sont libellés en USD mais les actifs sous-jacents peuvent être en EUR/GBP/autres. Le traitement fiscal dépend de votre situation et peut évoluer. Consultez un conseil indépendant." },
      ],
      statutory1: "Cette communication à caractère promotionnel est émise par Strategy Intelligence Solutions LTD, société immatriculée en Angleterre et au Pays de Galles. Elle est destinée uniquement aux investisseurs qualifiés (a) high-net-worth certifiés, (b) sophisticated investors certifiés, (c) self-certified sophisticated ou (d) restricted investors certifiés selon le Financial Services and Markets Act 2000 (Financial Promotion) Order 2005, ainsi qu'aux clients professionnels / contreparties éligibles au sens des règles de la FCA.",
      statutory2: "Aucun élément de ce site ne constitue une offre, sollicitation ou recommandation d'acheter ou de souscrire à un titre, et aucun élément ne doit être utilisé comme base d'une décision d'investissement. Les destinataires ne doivent pas considérer le contenu comme un conseil juridique, fiscal ou d'investissement et sont invités à consulter leurs propres conseils professionnels.",
      statutory3: "En poursuivant, vous confirmez que vous entrez dans l'une des catégories ci-dessus, que vous avez lu cet avertissement dans son intégralité et que vous comprenez que la valeur de tout investissement peut aussi bien baisser que monter.",
    },
    schedule: {
      eyebrow: "Planifier un appel",
      title: "Échangez avec notre équipe relations investisseurs.",
      body: "Une conversation de 30 minutes pour parcourir l'opportunité, répondre à vos questions et — si approprié — discuter des prochaines étapes. Sans pression, sans obligation.",
      duration: "30 minutes · Visio ou téléphone",
      callbackNote: "Vous préférez qu'on vous rappelle ? Répondez à votre email de confirmation avec un créneau souhaité.",
      disclaimer: "Cet appel ne constitue pas un conseil en investissement. Les informations partagées le sont à titre illustratif uniquement.",
      embedHint: "Le calendrier ne s'affiche pas ?",
      openExternal: "Ouvrir dans une nouvelle fenêtre",
    },
    about: {
      eyebrow: "Présentation",
      title: "Un opérateur britannique d'infrastructure IA européenne.",
      body: "Strategy Intelligence Solutions LTD a été fondée pour consolider l'offre fragmentée d'infrastructure prête pour l'IA en Europe. Nous adoptons une approche opérationnelle à la Brookfield — intégration verticale profonde, souscription disciplinée et horizons de détention pluridécennaux.",
      missionTitle: "Mission",
      missionBody: "Concevoir, acquérir et exploiter l'infrastructure IA de nouvelle génération à travers l'Europe.",
      visionTitle: "Vision",
      visionBody: "Devenir l'une des principales sociétés indépendantes d'infrastructure IA d'Europe au cours de la décennie.",
      valuesTitle: "Valeurs",
      values: [
        { t: "Excellence d'ingénierie", d: "Chaque mégawatt est conçu, pas acheté." },
        { t: "Discipline institutionnelle", d: "Gouvernance, transparence et gestion des risques avant croissance." },
        { t: "Responsabilité environnementale", d: "Stratégie renouvelable fondée sur l'additionnalité." },
        { t: "Horizon long", d: "Une réflexion infrastructure sur plusieurs décennies." },
      ],
      timelineTitle: "Jalons sélectionnés",
    },
    vision: {
      eyebrow: "Vision",
      title: "La couche de calcul de l'économie du XXIᵉ siècle.",
      body: "L'intelligence artificielle devient l'intrant industriel majeur des cinquante prochaines années. L'infrastructure qui la soutient — énergie, foncier, eau, silicium et fibre — constitue une classe d'actifs stratégique. Nous la construisons, en Europe, pour des contreparties européennes.",
      pillarsTitle: "Trois horizons",
      pillars: [
        { code: "H1", title: "2024 – 2027", body: "Établir une échelle opérationnelle sur cinq sites phares. Consolider une empreinte européenne fibre et énergie. Obtenir les certifications de gouvernance de niveau institutionnel." },
        { code: "H2", title: "2027 – 2032", body: "Atteindre l'échelle du gigawatt. Étendre les régions cloud souveraines. Intégrer verticalement la production renouvelable sur site et le stockage longue durée." },
        { code: "H3", title: "2032 – 2040", body: "Exploiter l'une des plus grandes plateformes de calcul indépendantes d'Europe. Assurer la circularité énergétique structurelle des sites. Faire émerger une génération de champions européens de l'IA." },
      ],
      thesisTitle: "Thèse d'investissement",
      thesisBody: "La transition IA est contrainte par le calcul, par l'énergie et par la géographie. L'Europe ne peut pas dépendre d'un calcul souverain étranger. Des opérateurs indépendants, bien capitalisés et pilotés par l'ingénierie sont essentiels.",
    },
    leadership: {
      eyebrow: "Direction",
      title: "Opérateurs, ingénieurs, allocataires.",
      body: "Une équipe dirigeante combinant expérience opérationnelle à l'échelle hyperscale, discipline institutionnelle du capital et rigueur d'ingénierie.",
      board: "Conseil d'administration",
      execTeam: "Comité exécutif",
      advisors: "Comité consultatif",
      members: [
        { name: "A. Whitmore", role: "Directeur général", bio: "Vingt ans de mise à l'échelle de la colocation hyperscale et de l'infrastructure réseau à travers l'EMEA.", area: "exec" },
        { name: "M. Laurent", role: "Directrice financière", bio: "Anciennement responsable infrastructure au sein d'un allocataire européen. Titulaire du CFA.", area: "exec" },
        { name: "R. Nováková", role: "Directrice des opérations", bio: "Deux décennies dans les opérations d'installations critiques. Certifiée Uptime Institute AOS.", area: "exec" },
        { name: "K. Fernández", role: "Directeur de la technologie", bio: "Ancien ingénieur principal d'une plateforme hyperscale d'entraînement IA.", area: "exec" },
        { name: "S. Al-Rashid", role: "Directeur de la durabilité", bio: "Spécialiste de la transition énergétique, précédemment au sein d'un utility européen.", area: "exec" },
        { name: "D. Bergström", role: "Président indépendant", bio: "Président d'audit dans deux opérateurs d'infrastructure du FTSE 250.", area: "board" },
        { name: "H. Okafor", role: "Administrateur non exécutif", bio: "Ancien sous-gouverneur d'autorité monétaire. Expertise en risque institutionnel.", area: "board" },
        { name: "L. Costa", role: "Administratrice non exécutive", bio: "Associée gérante d'un cabinet de conseil européen aux fonds de pension.", area: "board" },
      ],
    },
    sustainability: {
      eyebrow: "Durabilité",
      title: "Additionnalité d'abord, comptabilité ensuite.",
      body: "Notre stratégie environnementale repose sur une fourniture d'énergie renouvelable additionnelle, synchronisée temporellement et géographiquement. Nous refusons les moyennes annualisées et les certificats non liés.",
      pillarsTitle: "Cadre",
      pillars: [
        { t: "Énergie décarbonée 24/7", d: "Approvisionnement renouvelable synchronisé à l'heure sur tous les sites opérationnels d'ici 2027." },
        { t: "Opérations water-positive", d: "Architectures de refroidissement en circuit fermé. Zéro eau potable sur les nouveaux campus." },
        { t: "Matériel circulaire", d: "Partenariats de reconditionnement serveurs, GPU et câbles. Traçabilité au composant." },
        { t: "Intégration communautaire", d: "Redistribution de la chaleur fatale aux réseaux de chaleur urbains lorsque la géographie le permet." },
      ],
      metricsTitle: "Objectifs 2026",
      metrics: [
        { k: "Plafond PUE", v: "1,15" },
        { k: "Couverture renouvelable", v: "100 %" },
        { k: "Efficacité usage de l'eau", v: "0,10 L/kWh" },
        { k: "Scope 1+2 net", v: "0 tCO₂e" },
      ],
      reportsTitle: "Rapports & publications",
      reportsBody: "Rapport ESG annuel, soumission CDP et publications climat alignées TCFD disponibles dans le centre documentaire.",
    },
    news: {
      eyebrow: "Salle de presse",
      title: "Actualités & communiqués.",
      body: "Jalons opérationnels, annonces financières, mises à jour de gouvernance et couverture presse.",
      readingTime: "min de lecture",
      items: [
        { date: "2026-02-14", category: "Opérationnel", title: "Nordic Data Lab atteint 42 MW de capacité mise en service.", body: "Le campus de Stockholm a achevé la mise en service complète de sa première phase, délivrant 42 MW de capacité prête pour l'IA avec un PUE mesuré de 1,08." },
        { date: "2026-01-30", category: "Corporate", title: "Strategy Intelligence Solutions confirme un pipeline de 220 MW pour 2027.", body: "La société confirme son pipeline de construction européen à 220 MW, répartis entre l'Espagne, le Royaume-Uni et les Pays-Bas." },
        { date: "2026-01-12", category: "Gouvernance", title: "Nomination d'un président indépendant au conseil.", body: "Le conseil accueille un président indépendant fort de deux décennies d'expérience dans des comités d'audit du FTSE 250." },
        { date: "2025-12-05", category: "Durabilité", title: "Accord d'approvisionnement CFE 24/7 signé pour les opérations nordiques.", body: "Un contrat pluriannuel d'approvisionnement renouvelable synchronisé a été finalisé avec un producteur hydroélectrique nordique." },
        { date: "2025-11-18", category: "Technologie", title: "Premiers pods B200 NVL72 préparés pour Iberia AI Campus.", body: "Les premiers systèmes de référence NVIDIA B200 ont été préparés pour intégration au sein de Iberia AI Campus à Madrid." },
      ],
    },
    careers: {
      eyebrow: "Carrières",
      title: "Bâtissez l'infrastructure des cinquante prochaines années.",
      body: "Nous recrutons ingénieurs, opérateurs et allocataires au Royaume-Uni, en Suède, en Espagne et aux Pays-Bas.",
      why: "Pourquoi nous rejoindre",
      whyItems: [
        { t: "Infrastructure réelle", d: "Vous construirez des systèmes critiques à l'échelle du mégawatt — pas des présentations." },
        { t: "Horizons longs", d: "Nous planifions sur plusieurs décennies. Les carrières peuvent couvrir un cycle de construction complet." },
        { t: "Discipline institutionnelle", d: "Gouvernance rigoureuse, décisions transparentes, culture adulte." },
        { t: "Actionnariat", d: "Participation au capital significative pour les rôles d'ingénierie, d'opérations et de direction." },
      ],
      openRoles: "Postes ouverts",
      roles: [
        { title: "Ingénieur principal data center", location: "Londres, UK", type: "Temps plein" },
        { title: "Responsable infrastructure électrique", location: "Stockholm, SE", type: "Temps plein" },
        { title: "Ingénieur senior HPC / clusters GPU", location: "Madrid, ES", type: "Temps plein" },
        { title: "Analyste durabilité & CFE", location: "Amsterdam, NL", type: "Temps plein" },
        { title: "Analyste relations investisseurs", location: "Londres, UK", type: "Temps plein" },
        { title: "SRE plateformes IA", location: "Aberdeen, UK", type: "Temps plein" },
      ],
      contact: "Candidatures générales",
      contactEmail: "careers@strategyintelligence.co.uk",
    },
    partners: {
      eyebrow: "Partenaires",
      title: "Un réseau de partenaires institutionnels sélectionnés.",
      body: "Nous travaillons avec des contreparties mondiales de l'infrastructure — des fournisseurs de silicium aux utilities, en passant par le capital souverain et les cabinets d'ingénierie indépendants.",
      categories: [
        { title: "Silicium & plateformes", items: ["Partenaire de référence NVIDIA (H200, B200)", "Alignement AMD Instinct", "Fabric Broadcom Tomahawk", "Mémoire haute bande passante Micron"] },
        { title: "Énergie & utilities", items: ["Producteurs hydroélectriques nordiques (contreparties PPA)", "Opérateurs éoliens offshore britanniques", "Solaires indépendants ibériques", "Intégrateurs européens de stockage batterie"] },
        { title: "Ingénierie & construction", items: ["Contractants mécaniques européens Tier 1", "Intégrateurs modulaires de data centers", "OEM spécialistes du refroidissement liquide", "Partenaires de fibre et de soudure OSP"] },
        { title: "Capital & conseil", items: ["Relation d'audit big four", "Cabinets juridiques magic circle", "Consultants infrastructure institutionnels", "Conseillers en risque indépendants"] },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Questions fréquentes.",
      body: "Les questions les plus courantes des contreparties institutionnelles, des journalistes et des candidats.",
      items: [
        {
          q: "Que construit concrètement Strategy Intelligence Solutions ?",
          a: "Nous concevons, finançons, construisons et exploitons l'infrastructure physique nécessaire à l'entraînement et à l'inférence des charges d'intelligence artificielle modernes. Cela couvre les data centers IA dédiés, les clusters GPU, les plateformes de calcul haute performance, les systèmes de refroidissement liquide, l'infrastructure électrique sur site, l'approvisionnement renouvelable, les backbones de fibre noire et les régions cloud souveraines.",
        },
        {
          q: "Où la société opère-t-elle ?",
          a: "Nous sommes domiciliés au Royaume-Uni et exploitons des actifs en Suède, en Espagne, au Royaume-Uni et aux Pays-Bas, avec un pipeline de construction paneuropéen de 220 MW ciblé pour livraison d'ici 2027.",
        },
        {
          q: "Proposez-vous des produits d'investissement aux investisseurs particuliers ?",
          a: "Strategy Intelligence Solutions LTD est ouverte aux investisseurs privés éligibles à partir de 10 000 $, aux côtés de contreparties institutionnelles. Nous ne procédons pas à une offre au public. L'accès est accordé après un examen de conformité, incluant vérification d'identité et, le cas échéant, classification en investisseur professionnel selon la réglementation locale. Les investisseurs potentiels sont invités à consulter leurs propres conseils sur l'adéquation.",
        },
        {
          q: "Quelle est votre approche de la durabilité ?",
          a: "Nous appliquons une stratégie renouvelable fondée sur l'additionnalité, avec un approvisionnement synchronisé temporellement et géographiquement. Notre objectif est une énergie décarbonée 24/7 sur tous les sites opérationnels d'ici 2027 et une posture water-positive sur les nouveaux campus.",
        },
        {
          q: "Avec quelles contreparties travaillez-vous ?",
          a: "Côté silicium et plateformes : NVIDIA, AMD, Broadcom et Micron. Côté énergie : producteurs hydroélectriques nordiques, opérateurs éoliens offshore britanniques et solaires indépendants ibériques. Notre équipe conseil inclut audit big four, cabinets juridiques magic circle et consultants infrastructure indépendants.",
        },
        {
          q: "Comment la société est-elle gouvernée ?",
          a: "La société est dotée d'un conseil présidé par un président indépendant, avec des comités d'audit et de risque dédiés. Le rythme de reporting est trimestriel pour les indicateurs opérationnels et semestriel pour les publications financières, avec un audit externe assuré par un cabinet big four.",
        },
        {
          q: "Comment demander l'accès au portail investisseur ?",
          a: "Les contreparties institutionnelles peuvent demander l'accès via la page d'inscription du portail. L'accès est octroyé après examen de conformité, incluant vérification d'identité et, le cas échéant, classification en investisseur professionnel.",
        },
        {
          q: "Mes données sont-elles sécurisées ?",
          a: "La plateforme est opérée en alignement avec les contrôles ISO 27001, conforme au RGPD et en cours d'attestation SOC 2. Toutes les informations personnelles sont chiffrées au repos et en transit, et les accès sont soumis à un journal d'audit complet.",
        },
      ],
    },
    infra: { eyebrow: "Infrastructure", title: "La fondation physique de l'IA européenne.", body: "Huit capacités verticalement intégrées, un modèle d'exploitation." },
    tech: { eyebrow: "Technologie", title: "Architectures de référence, du photon à l'inférence.", body: "Refroidissement, calcul, réseau et énergie — conçus comme un système unique." },
    projects: {
      eyebrow: "Projets",
      title: "Programmes d'infrastructure paneuropéens sélectionnés.",
      location: "Localisation",
      power: "Capacité électrique",
      status: "Statut",
      timeline: "Calendrier",
      progress: "Avancement",
      specs: "Spécifications techniques",
    },
    ir: {
      eyebrow: "Relations investisseurs",
      title: "Transparence de niveau institutionnel.",
      body: "Aperçu corporate, gouvernance, publications financières et documentation technique.",
      disclaimer: "Les informations présentes sur ce site sont fournies à titre informatif uniquement et ne constituent ni une offre de vente ni une sollicitation d'achat de titres. Aucun rendement n'est ici promis.",
      docs: "Centre documentaire",
      contactIR: "Contacter les relations investisseurs",
    },
    contact: {
      eyebrow: "Contact", title: "Échangez avec notre équipe.", body: "Pour les demandes institutionnelles, partenariats et presse.",
      name: "Nom complet", email: "Email professionnel", company: "Organisation", phone: "Téléphone (facultatif)",
      subject: "Sujet", message: "Message", submit: "Envoyer le message",
      success: "Merci — nous vous répondrons sous un jour ouvré.",
      error: "L'envoi du message a échoué. Veuillez réessayer.",
    },
    auth: {
      loginTitle: "Portail investisseur",
      loginSubtitle: "Connectez-vous à votre compte institutionnel.",
      registerTitle: "Demander l'accès au portail",
      registerSubtitle: "Complétez le formulaire ci-dessous pour ouvrir un compte institutionnel.",
      email: "Email", password: "Mot de passe",
      fullName: "Nom complet", companyField: "Organisation", countryField: "Pays",
      submit: "Se connecter", submitRegister: "Créer un compte",
      noAccount: "Pas encore d'accès ?", haveAccount: "Déjà un accès ?",
      invalid: "Identifiants invalides.",
    },
    portal: {
      welcome: "Bon retour",
      dashboard: "Tableau de bord",
      portfolio: "Portefeuille",
      documents: "Documents",
      kyc: "Vérification d'identité",
      withdrawals: "Demandes de retrait",
      messages: "Messages",
      profile: "Profil",
      overview: "Vue d'ensemble",
      committed: "Total engagé",
      currentValue: "Valeur actuelle",
      unrealized: "Latent",
      allocation: "Allocation",
      performance: "Performance du portefeuille",
      kycPending: "Vérification en attente",
      kycReview: "En cours d'examen",
      kycApproved: "Vérifié",
      kycRejected: "Vérification rejetée",
      kycStart: "Commencer la vérification",
      newWithdrawal: "Nouvelle demande de retrait",
      amount: "Montant", currency: "Devise",
      bank: "Nom de la banque", iban: "IBAN", reason: "Motif (facultatif)",
      submit: "Envoyer la demande",
      noWithdrawals: "Aucune demande de retrait pour le moment.",
      unread: "non lu(s)",
    },
    admin: {
      title: "Administration",
      overview: "Vue d'ensemble",
      investors: "Investisseurs",
      projectsMgmt: "Projets",
      docsMgmt: "Documents",
      audit: "Journaux d'audit",
      contactMsgs: "Messages de contact",
      approve: "Approuver",
      reject: "Rejeter",
      pending: "en attente",
      kycReview: "En examen",
    },
    ai: {
      title: "Athéna — Assistant IA",
      subtitle: "Interrogez notre infrastructure, notre technologie ou nos projets.",
      placeholder: "Parlez à Athéna…",
      hint: "Propulsé par Strategy Intelligence Solutions",
      requiresAuth: "Connectez-vous pour discuter avec Athéna.",
    },
    footer: {
      tagline: "Infrastructure institutionnelle pour l'ère de l'IA.",
      hq: "Siège social · Royaume-Uni",
      legal: "Mentions légales", privacy: "Confidentialité", cookies: "Cookies", terms: "Conditions",
      copyright: "Strategy Intelligence Solutions LTD. Tous droits réservés.",
    },
  },

  es: {
    common: {
      exploreInfra: "Explorar infraestructura",
      ourProjects: "Nuestros proyectos",
      investorPortal: "Portal del inversor",
      learnMore: "Saber más",
      contactUs: "Contáctenos",
      getInTouch: "Contactar",
      downloadPdf: "Descargar PDF",
      readMore: "Leer más",
      viewAll: "Ver todo",
      login: "Iniciar sesión",
      register: "Solicitar acceso",
      logout: "Cerrar sesión",
      language: "Idioma",
      requestAccess: "Solicitar acceso",
      backToHome: "Volver al inicio",
      readAll: "Leer el informe completo",
      applyNow: "Postular",
      openPosition: "Puesto abierto",
      seeAll: "Ver todo",
    },
    nav: {
      home: "Inicio",
      company: "Compañía",
      about: "Presentación",
      vision: "Visión",
      leadership: "Liderazgo",
      sustainability: "Sostenibilidad",
      careers: "Carreras",
      infrastructure: "Infraestructura",
      technology: "Tecnología",
      projects: "Proyectos",
      investors: "Inversores",
      investorRelations: "Relaciones con inversores",
      partners: "Socios",
      news: "Actualidad",
      faq: "FAQ",
      contact: "Contacto",
    },
    hero: {
      eyebrow: "Strategy Intelligence Solutions LTD — Reino Unido",
      title: "Construyendo la infraestructura de IA de próxima generación en Europa.",
      subtitle: "El cimiento físico de la inteligencia artificial — del fotón a la inferencia. Data centers, clústeres GPU, HPC, energía y fibra, operados según estándares institucionales.",
    },
    stats: {
      operational: "MW en operación",
      pipeline: "MW en pipeline",
      sites: "sitios europeos",
      pue: "PUE promedio",
    },
    marquee: "Marco fiable · Alineado ISO 27001 · Uptime Institute · RGPD · SOC 2 en curso · Certificado CE · Tier III+ · CISPE",
    home: {
      pillarsEyebrow: "Lo que construimos",
      pillarsTitle: "Seis capacidades verticalmente integradas.",
      pillarsIntro: "Diseñamos, financiamos, construimos y operamos cada capa de la pila de IA — desde la subestación al acelerador.",
      pillars: [
        { title: "Data centers de IA", desc: "Campus Tier III+ diseñados para cargas de entrenamiento densas, dimensionados para 100 kW/rack y más." },
        { title: "Clústeres GPU y HPC", desc: "Plataformas NVIDIA H200, B200 y de próxima generación desplegadas como arquitecturas de referencia, operativas desde el primer día." },
        { title: "Refrigeración líquida", desc: "Bucles direct-to-chip e inmersión con PUE inferior a 1,10 y operaciones neutras en agua." },
        { title: "Infraestructura eléctrica", desc: "Interconexiones de red, subestaciones y baterías dimensionadas para trayectorias gigavatio." },
        { title: "Energías renovables", desc: "PPAs con productores hidroeléctricos, eólicos y solares en toda Europa — verificables, adicionales y sincronizados temporalmente." },
        { title: "Fibra y cloud", desc: "Backbones de fibra oscura y regiones cloud soberanas al servicio de industrias reguladas en la UE y el Reino Unido." },
      ],
      projectsEyebrow: "Proyectos seleccionados",
      projectsTitle: "Un portafolio paneuropeo, diseñado para escala.",
      investorEyebrow: "Relaciones con inversores",
      investorTitle: "Transparencia de nivel institucional.",
      investorBody: "Reporting corporativo integral, divulgaciones de gobernanza y documentación técnica — diseñados para contrapartes profesionales.",
      ctaEyebrow: "Asóciese con nosotros",
      ctaTitle: "Capital de largo plazo desplegado en infraestructura física de IA.",
      ctaBody: "Damos la bienvenida al diálogo con socios de capital soberano, fondos de pensiones, aseguradoras y family offices alineados con una tesis de infraestructura pluri-decadal.",
    },
    home2: {
      hero: {
        eyebrow: "Strategy Intelligence Solutions LTD — Reino Unido",
        title: "Invierta en la infraestructura que impulsa la revolución de la IA.",
        subtitle: "Acceda a inversiones en infraestructura de IA gestionadas profesionalmente, desde 10 000 $. Sea propietario de una parte de los data centers, clústeres GPU y energías renovables que construyen la próxima generación europea del cómputo.",
        ctaPrimary: "Ser inversor",
        ctaSecondary: "Saber más",
        disclaimer: "Reservado a inversores privados elegibles · No constituye una oferta pública · Sujeto a elegibilidad",
      },
      trust: [
        { k: "Mínimo", v: "Desde 10 000 $" },
        { k: "Gestión", v: "Gestionado profesionalmente" },
        { k: "Sector", v: "Infraestructura de IA" },
        { k: "Reporting", v: "Totalmente transparente" },
        { k: "Estructura", v: "Nivel institucional" },
      ],
      what: {
        eyebrow: "¿Qué es Titan?",
        title: "Una forma sencilla de poseer una parte de la columna vertebral IA de Europa.",
        body: "Titan construye y opera la infraestructura física que entrena y ejecuta la inteligencia artificial — y permite a inversores privados elegibles participar.",
      },
      whatItems: [
        { t: "Activos reales", d: "Data centers, clústeres GPU, redes de fibra y energía renovable — activos que puede localizar en un mapa." },
        { t: "Crecimiento a largo plazo", d: "Inversiones estructuradas para un ciclo plurianual de construcción de infraestructura de IA. Sin especulación a corto plazo." },
        { t: "Gestionado por usted", d: "Un equipo dedicado de operaciones y relaciones con inversores lo maneja todo, desde la tecnología hasta el reporting." },
      ],
      why: {
        eyebrow: "¿Por qué ahora?",
        title: "La IA es sedienta de cómputo. Europa está infra-construida.",
      },
      whyItems: [
        { stat: "+300 %", label: "Demanda de cómputo IA para 2030", body: "El cómputo global de entrenamiento de IA se duplica cada 6 meses. Europa enfrenta una brecha estructural de infraestructura." },
        { stat: "200 Md€+", label: "Inversión europea hasta 2030", body: "Las estimaciones independientes sitúan la inversión necesaria en data centers de IA europeos por encima de 200 mil millones de euros esta década." },
        { stat: "≤ 5 %", label: "Cuota de mercado europea hoy", body: "Europa alberga menos del 5 % de la capacidad global de entrenamiento IA — una brecha que operadores independientes como Titan están diseñados para cerrar." },
      ],
      steps: {
        eyebrow: "Cómo funciona",
        title: "Cuatro pasos hasta su primera inversión.",
        body: "Todo el proceso lleva menos de 30 minutos en línea. Nuestro equipo está disponible en cada paso.",
      },
      steps_list: [
        { stepLabel: "Paso 01", title: "Cree su cuenta", body: "Regístrese con su email y configure el acceso seguro a su portal privado de inversor.", time: "2 minutos" },
        { stepLabel: "Paso 02", title: "Verifique su identidad", body: "Suba un pasaporte o documento de identidad y un comprobante de domicilio. Nuestro equipo de cumplimiento revisa en 24 horas.", time: "5 minutos" },
        { stepLabel: "Paso 03", title: "Financie su inversión", body: "Elija el monto (10 000 $ o más) y financie mediante transferencia bancaria. Recibirá la documentación completa.", time: "10 minutos" },
        { stepLabel: "Paso 04", title: "Reciba reporting continuo", body: "Actualizaciones trimestrales de rendimiento, informes de proyecto y acceso a la Data Room segura.", time: "Continuo" },
      ],
      human: {
        eyebrow: "El equipo",
        title: "Personas reales. Infraestructura real. Responsabilidad real.",
        body: "Somos ingenieros, operadores e inversores que hemos construido y operado infraestructura crítica durante dos décadas. Cuando invierte con Titan, sabe en quién confía.",
        cta: "Conocer al liderazgo",
        badgeEyebrow: "Sede",
        badgeText: "Londres, Reino Unido — con operaciones en Suecia, España y Países Bajos.",
      },
      infra: {
        eyebrow: "Donde trabaja su capital",
        title: "Cada megavatio se diseña, monitoriza y reporta con estándares institucionales.",
      },
      finalCta: {
        title: "¿Listo para invertir en la infraestructura IA del mañana?",
        body: "Abra una cuenta en minutos o programe una llamada con nuestro equipo de relaciones con inversores para hablar de sus objetivos.",
        next: "Su siguiente paso",
        scheduleCall: "Programar una llamada",
        download: "Leer la presentación al inversor",
        disclaimer: "Toda inversión conlleva riesgos. Los rendimientos pasados no son indicativos de resultados futuros. No constituye una oferta pública.",
      },
      cases: {
        eyebrow: "Historias de inversores",
        title: "Cómo nuestros miembros ponen su capital a trabajar.",
        body: "Experiencias seleccionadas de inversores privados y profesionales — compartidas con su permiso y anonimizadas si lo solicitan.",
        items: [
          { name: "M.C.", role: "Principal de family office", location: "Londres, UK", allocation: "85 000 $", tenure: "18 meses",
            quote: "El nivel de transparencia es poco común a este ticket. Las actualizaciones trimestrales parecen informes institucionales y la Data Room es realmente útil. Sé exactamente dónde está mi capital." },
          { name: "P.K.", role: "Ejecutivo tecnológico", location: "Zúrich, CH", allocation: "25 000 $", tenure: "9 meses",
            quote: "Quería exposición al build-out IA sin picking de acciones. Titan me permitió respaldar los megavatios reales. El onboarding fue directo y el KYC más rápido que el de mi banco." },
          { name: "S.R.", role: "Inversor privado", location: "Ámsterdam, NL", allocation: "40 000 $", tenure: "12 meses",
            quote: "Infraestructura real, reporting real, personas reales al teléfono cuando tengo preguntas. Se parece más a una plataforma de mercados privados que a cualquier cosa que haya usado antes." },
        ],
        note: "Testimonios compartidos voluntariamente. No indicativos de rendimientos futuros. Nombres y fotografías anonimizados a petición de cada inversor.",
      },
    },
    risk: {
      eyebrow: "Advertencia de riesgo",
      pageTitle: "Advertencia sobre riesgos de inversión.",
      headline: "No invierta a menos que esté preparado para perder todo el dinero invertido.",
      body: "Se trata de una inversión de alto riesgo. Es poco probable que esté protegido por un regulador o régimen de compensación si algo sale mal. Dedique 2 minutos a saber más.",
      readMore: "Leer la advertencia completa →",
      keyRisksTitle: "Riesgos clave que debe entender",
      bullets: [
        { t: "Pérdida total posible", d: "Las inversiones en infraestructura y activos privados pueden bajar tanto como subir. En el peor de los casos, puede perder el 100 % del dinero invertido." },
        { t: "Iliquidez", d: "Probablemente no podrá vender su inversión cuando quiera. No hay mercado secundario establecido y los reembolsos pueden estar restringidos." },
        { t: "Falta de protección", d: "El UK Financial Services Compensation Scheme (FSCS) no suele cubrir este tipo de inversión. Es poco probable que tenga acceso al Financial Ombudsman Service." },
        { t: "Sin dividendos ni garantías de rendimiento", d: "Cualquier proyección es meramente ilustrativa y no garantiza rendimientos futuros. Las distribuciones dependen del desempeño operativo." },
        { t: "Concentración y riesgo sectorial", d: "El sector de infraestructura IA es intensivo en capital, expuesto a costos energéticos, permisos, cadena de suministro y obsolescencia tecnológica." },
        { t: "Divisa, fiscalidad y legal", d: "Los importes se muestran en USD pero los activos subyacentes pueden estar en EUR/GBP/otras divisas. El tratamiento fiscal depende de su situación y puede cambiar. Consulte con asesores independientes." },
      ],
      statutory1: "Esta comunicación de carácter promocional es emitida por Strategy Intelligence Solutions LTD, sociedad registrada en Inglaterra y Gales. Se dirige únicamente a inversores que califiquen como (a) high-net-worth certificados, (b) sophisticated investors certificados, (c) self-certified sophisticated o (d) restricted investors certificados según el Financial Services and Markets Act 2000 (Financial Promotion) Order 2005, así como a clientes profesionales / contrapartes elegibles según las normas de la FCA.",
      statutory2: "Nada en este sitio constituye una oferta, solicitación o recomendación de comprar o suscribir ningún valor, y nada debe usarse como base para una decisión de inversión. Los destinatarios no deben tratar el contenido como asesoramiento legal, fiscal o de inversión y se recomienda consultar a sus propios asesores profesionales.",
      statutory3: "Al continuar, confirma que cumple una de las categorías anteriores, que ha leído esta advertencia en su totalidad y que entiende que el valor de cualquier inversión puede tanto subir como bajar.",
    },
    schedule: {
      eyebrow: "Programar una llamada",
      title: "Hable con nuestro equipo de relaciones con inversores.",
      body: "Una conversación de 30 minutos para revisar la oportunidad, responder sus preguntas y — si procede — hablar de los próximos pasos. Sin presión, sin obligación.",
      duration: "30 minutos · Video o teléfono",
      callbackNote: "¿Prefiere que le llamemos? Responda a su email de confirmación con un horario preferido.",
      disclaimer: "Esta llamada no constituye asesoramiento de inversión. Cualquier información compartida es meramente ilustrativa.",
      embedHint: "¿El calendario no carga?",
      openExternal: "Abrir en una nueva ventana",
    },
    about: {
      eyebrow: "Presentación",
      title: "Un operador con sede en Reino Unido de infraestructura IA europea.",
      body: "Strategy Intelligence Solutions LTD se constituyó para consolidar la oferta fragmentada de infraestructura preparada para IA en Europa. Adoptamos un enfoque operativo al estilo Brookfield — integración vertical profunda, disciplina de suscripción y horizontes de tenencia pluri-decadales.",
      missionTitle: "Misión",
      missionBody: "Diseñar, adquirir y operar infraestructura de IA de próxima generación en toda Europa.",
      visionTitle: "Visión",
      visionBody: "Convertirnos en una de las principales compañías independientes de infraestructura IA de Europa esta década.",
      valuesTitle: "Valores",
      values: [
        { t: "Excelencia de ingeniería", d: "Cada megavatio se diseña, no se compra." },
        { t: "Disciplina institucional", d: "Gobernanza, transparencia y gestión de riesgos antes que crecimiento." },
        { t: "Responsabilidad ambiental", d: "Estrategia renovable basada en la adicionalidad." },
        { t: "Horizonte largo", d: "Pensamiento de infraestructura pluri-decadal." },
      ],
      timelineTitle: "Hitos seleccionados",
    },
    vision: {
      eyebrow: "Visión",
      title: "La capa de cómputo de la economía del siglo XXI.",
      body: "La inteligencia artificial se está convirtiendo en el principal insumo industrial de los próximos cincuenta años. La infraestructura que la sostiene — energía, suelo, agua, silicio y fibra — es una clase de activos estratégica. La estamos construyendo, en Europa, para contrapartes europeas.",
      pillarsTitle: "Tres horizontes",
      pillars: [
        { code: "H1", title: "2024 – 2027", body: "Establecer escala operativa en cinco sitios insignia. Consolidar una huella europea de fibra y energía. Obtener certificaciones de gobernanza institucional." },
        { code: "H2", title: "2027 – 2032", body: "Escalar a un portafolio de nivel gigavatio. Extender las regiones cloud soberanas. Integrar verticalmente la generación renovable in-situ y el almacenamiento de larga duración." },
        { code: "H3", title: "2032 – 2040", body: "Operar una de las mayores plataformas de cómputo independientes de Europa. Alcanzar circularidad energética estructural. Impulsar una generación de campeones europeos de IA." },
      ],
      thesisTitle: "Tesis de inversión",
      thesisBody: "La transición IA está limitada por el cómputo, la energía y la geografía. Europa no puede depender de un cómputo soberano extranjero. Los operadores independientes, bien capitalizados y liderados por ingeniería son esenciales.",
    },
    leadership: {
      eyebrow: "Liderazgo",
      title: "Operadores, ingenieros, asignadores de capital.",
      body: "Un equipo directivo que combina experiencia operativa a escala hyperscale, disciplina institucional del capital y rigor de ingeniería.",
      board: "Consejo de administración",
      execTeam: "Comité ejecutivo",
      advisors: "Comité asesor",
      members: [
        { name: "A. Whitmore", role: "Consejero delegado", bio: "Veinte años escalando colocation hyperscale e infraestructura de red en EMEA.", area: "exec" },
        { name: "M. Laurent", role: "Directora financiera", bio: "Anteriormente responsable de infraestructura en un asignador europeo. Titular del CFA.", area: "exec" },
        { name: "R. Nováková", role: "Directora de operaciones", bio: "Dos décadas en operaciones de instalaciones críticas. Certificada Uptime Institute AOS.", area: "exec" },
        { name: "K. Fernández", role: "Director de tecnología", bio: "Ex ingeniero principal de una plataforma hyperscale de entrenamiento IA.", area: "exec" },
        { name: "S. Al-Rashid", role: "Director de sostenibilidad", bio: "Especialista en transición energética, previamente en una utility europea.", area: "exec" },
        { name: "D. Bergström", role: "Presidente independiente", bio: "Presidente de auditoría en dos operadores de infraestructura del FTSE 250.", area: "board" },
        { name: "H. Okafor", role: "Consejero no ejecutivo", bio: "Ex subgobernador de autoridad monetaria. Experiencia en riesgo institucional.", area: "board" },
        { name: "L. Costa", role: "Consejera no ejecutiva", bio: "Socia gestora en una consultora europea de fondos de pensiones.", area: "board" },
      ],
    },
    sustainability: {
      eyebrow: "Sostenibilidad",
      title: "Adicionalidad primero, contabilidad después.",
      body: "Nuestra estrategia ambiental se basa en un suministro renovable adicional, sincronizado temporal y geográficamente. No aceptamos promedios anualizados ni certificados desagregados.",
      pillarsTitle: "Marco",
      pillars: [
        { t: "Energía descarbonizada 24/7", d: "Suministro renovable sincronizado por hora en todos los sitios operativos para 2027." },
        { t: "Operaciones water-positive", d: "Arquitecturas de refrigeración en circuito cerrado. Cero agua potable en nuevos campus." },
        { t: "Hardware circular", d: "Alianzas de reacondicionamiento de servidores, GPUs y cableado. Trazabilidad a nivel de componente." },
        { t: "Integración comunitaria", d: "Redistribución de calor residual a redes de calor urbanas cuando la geografía lo permite." },
      ],
      metricsTitle: "Objetivos 2026",
      metrics: [
        { k: "Techo PUE", v: "1,15" },
        { k: "Cobertura renovable", v: "100 %" },
        { k: "Eficiencia uso del agua", v: "0,10 L/kWh" },
        { k: "Scope 1+2 neto", v: "0 tCO₂e" },
      ],
      reportsTitle: "Informes y divulgaciones",
      reportsBody: "Informe ESG anual, envío CDP y divulgaciones climáticas alineadas TCFD disponibles en el centro de documentos.",
    },
    news: {
      eyebrow: "Sala de prensa",
      title: "Actualidad y comunicados.",
      body: "Hitos operativos, anuncios financieros, actualizaciones de gobernanza y cobertura de prensa.",
      readingTime: "min de lectura",
      items: [
        { date: "2026-02-14", category: "Operativo", title: "Nordic Data Lab alcanza 42 MW de capacidad puesta en servicio.", body: "El campus de Estocolmo ha completado la puesta en servicio total de su primera fase, entregando 42 MW de capacidad lista para IA con un PUE medido de 1,08." },
        { date: "2026-01-30", category: "Corporativo", title: "Strategy Intelligence Solutions confirma un pipeline de 220 MW para 2027.", body: "La compañía confirma su pipeline de construcción europeo en 220 MW, repartidos entre España, Reino Unido y Países Bajos." },
        { date: "2026-01-12", category: "Gobernanza", title: "Nombramiento de un presidente independiente para el consejo.", body: "El consejo da la bienvenida a un presidente independiente con dos décadas de experiencia en comités de auditoría del FTSE 250." },
        { date: "2025-12-05", category: "Sostenibilidad", title: "Acuerdo de suministro CFE 24/7 firmado para las operaciones nórdicas.", body: "Se ha finalizado un contrato plurianual de suministro renovable sincronizado con un productor hidroeléctrico nórdico." },
        { date: "2025-11-18", category: "Tecnología", title: "Primeros pods B200 NVL72 preparados para Iberia AI Campus.", body: "Los primeros sistemas de referencia NVIDIA B200 se han preparado para su integración en Iberia AI Campus en Madrid." },
      ],
    },
    careers: {
      eyebrow: "Carreras",
      title: "Construya la infraestructura de los próximos cincuenta años.",
      body: "Contratamos ingenieros, operadores y asignadores en Reino Unido, Suecia, España y Países Bajos.",
      why: "Por qué unirse",
      whyItems: [
        { t: "Infraestructura real", d: "Construirá sistemas críticos a escala de megavatio — no diapositivas." },
        { t: "Horizontes largos", d: "Planificamos por décadas. Las carreras pueden abarcar un ciclo de construcción entero." },
        { t: "Disciplina institucional", d: "Gobernanza rigurosa, decisiones transparentes, cultura adulta." },
        { t: "Participación accionarial", d: "Equity significativo en ingeniería, operaciones y liderazgo." },
      ],
      openRoles: "Puestos abiertos",
      roles: [
        { title: "Ingeniero Principal de Data Center", location: "Londres, UK", type: "Tiempo completo" },
        { title: "Responsable de Infraestructura Eléctrica", location: "Estocolmo, SE", type: "Tiempo completo" },
        { title: "Ingeniero Senior HPC / Clústeres GPU", location: "Madrid, ES", type: "Tiempo completo" },
        { title: "Analista de Sostenibilidad y CFE", location: "Ámsterdam, NL", type: "Tiempo completo" },
        { title: "Asociado de Relaciones con Inversores", location: "Londres, UK", type: "Tiempo completo" },
        { title: "SRE de Plataformas IA", location: "Aberdeen, UK", type: "Tiempo completo" },
      ],
      contact: "Candidaturas generales",
      contactEmail: "careers@strategyintelligence.co.uk",
    },
    partners: {
      eyebrow: "Socios",
      title: "Una red curada de socios institucionales.",
      body: "Trabajamos con contrapartes globales de infraestructura — desde proveedores de silicio y utilities hasta capital soberano y firmas de ingeniería independientes.",
      categories: [
        { title: "Silicio y plataformas", items: ["Socio de referencia NVIDIA (H200, B200)", "Alineación AMD Instinct", "Fabric Broadcom Tomahawk", "Memoria de alto ancho de banda Micron"] },
        { title: "Energía y utilities", items: ["Productores hidroeléctricos nórdicos (contrapartes PPA)", "Operadores eólicos offshore británicos", "Solares independientes ibéricos", "Integradores europeos de almacenamiento por baterías"] },
        { title: "Ingeniería y construcción", items: ["Contratistas mecánicos europeos Tier 1", "Integradores modulares de data centers", "OEMs especialistas en refrigeración líquida", "Socios de fibra y empalme OSP"] },
        { title: "Capital y asesores", items: ["Relación de auditoría big four", "Bufetes magic circle", "Consultores institucionales de infraestructura", "Asesores de riesgo independientes"] },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Preguntas frecuentes.",
      body: "Las preguntas más comunes de contrapartes institucionales, periodistas y candidatos.",
      items: [
        {
          q: "¿Qué construye realmente Strategy Intelligence Solutions?",
          a: "Diseñamos, financiamos, construimos y operamos la infraestructura física necesaria para entrenar y servir cargas de inteligencia artificial modernas. Esto incluye data centers de IA especializados, clústeres GPU, plataformas de computación de alto rendimiento, sistemas de refrigeración líquida, infraestructura eléctrica in-situ, suministro renovable, backbones de fibra oscura y regiones cloud soberanas.",
        },
        {
          q: "¿Dónde opera la compañía?",
          a: "Estamos domiciliados en Reino Unido y operamos activos en Suecia, España, Reino Unido y Países Bajos, con un pipeline de construcción paneuropeo de 220 MW previsto para entregar en 2027.",
        },
        {
          q: "¿Ofrecen productos de inversión a inversores minoristas?",
          a: "Strategy Intelligence Solutions LTD está abierta a inversores privados elegibles desde 10 000 $, junto con contrapartes institucionales. No realizamos una oferta pública. El acceso se concede tras una revisión de cumplimiento, incluida la verificación de identidad y, cuando corresponda, la clasificación como inversor profesional según la normativa local. Los inversores potenciales deben consultar a sus propios asesores sobre la adecuación.",
        },
        {
          q: "¿Cuál es su enfoque de sostenibilidad?",
          a: "Aplicamos una estrategia renovable basada en la adicionalidad, con suministro sincronizado temporal y geográficamente. Nuestro objetivo es energía descarbonizada 24/7 en todos los sitios operativos para 2027 y una postura water-positive en los nuevos campus.",
        },
        {
          q: "¿Con qué contrapartes trabajan?",
          a: "En silicio y plataformas: NVIDIA, AMD, Broadcom y Micron. En energía: productores hidroeléctricos nórdicos, operadores eólicos offshore británicos y solares independientes ibéricos. Nuestro equipo asesor incluye auditoría big four, bufetes magic circle y consultores independientes de infraestructura.",
        },
        {
          q: "¿Cómo se gobierna la compañía?",
          a: "La compañía opera bajo un consejo presidido por un presidente independiente, con comités dedicados de auditoría y riesgo. La cadencia de reporting es trimestral para métricas operativas y semestral para publicaciones financieras, con auditoría externa realizada por una firma big four.",
        },
        {
          q: "¿Cómo puedo solicitar acceso al portal del inversor?",
          a: "Las contrapartes institucionales pueden solicitar acceso a través de la página de registro del portal. El acceso se concede tras una revisión de cumplimiento, que incluye verificación de identidad y, cuando corresponda, clasificación como inversor profesional.",
        },
        {
          q: "¿Están seguros mis datos?",
          a: "La plataforma opera alineada con los controles ISO 27001, es conforme al RGPD y está en proceso de atestación SOC 2. Toda la información personal está cifrada en reposo y en tránsito, y los accesos están sujetos a registro de auditoría completo.",
        },
      ],
    },
    infra: { eyebrow: "Infraestructura", title: "El cimiento físico de la IA europea.", body: "Ocho capacidades verticalmente integradas, un modelo operativo." },
    tech: { eyebrow: "Tecnología", title: "Arquitecturas de referencia, del fotón a la inferencia.", body: "Refrigeración, cómputo, red y energía — diseñados como un solo sistema." },
    projects: {
      eyebrow: "Proyectos", title: "Programas de infraestructura paneuropeos seleccionados.",
      location: "Ubicación", power: "Capacidad eléctrica", status: "Estado", timeline: "Cronograma",
      progress: "Progreso", specs: "Especificaciones técnicas",
    },
    ir: {
      eyebrow: "Relaciones con inversores", title: "Transparencia de nivel institucional.",
      body: "Visión corporativa, gobernanza, publicaciones financieras y documentación técnica.",
      disclaimer: "La información en este sitio se proporciona únicamente con fines informativos y no constituye una oferta de venta ni una solicitud de compra de valores. No se prometen rendimientos.",
      docs: "Centro de documentos",
      contactIR: "Contactar Relaciones con inversores",
    },
    contact: {
      eyebrow: "Contacto", title: "Hable con nuestro equipo.", body: "Para consultas institucionales, alianzas y prensa.",
      name: "Nombre completo", email: "Email profesional", company: "Organización", phone: "Teléfono (opcional)",
      subject: "Asunto", message: "Mensaje", submit: "Enviar mensaje",
      success: "Gracias — le responderemos en un día hábil.",
      error: "No se pudo enviar el mensaje. Inténtelo de nuevo.",
    },
    auth: {
      loginTitle: "Portal del inversor",
      loginSubtitle: "Inicie sesión en su cuenta institucional.",
      registerTitle: "Solicitar acceso al portal",
      registerSubtitle: "Complete el formulario a continuación para abrir una cuenta institucional.",
      email: "Email", password: "Contraseña",
      fullName: "Nombre completo", companyField: "Organización", countryField: "País",
      submit: "Iniciar sesión", submitRegister: "Crear cuenta",
      noAccount: "¿Sin acceso todavía?", haveAccount: "¿Ya tiene acceso?",
      invalid: "Credenciales inválidas.",
    },
    portal: {
      welcome: "Bienvenido de nuevo",
      dashboard: "Panel", portfolio: "Cartera", documents: "Documentos",
      kyc: "Verificación de identidad", withdrawals: "Solicitudes de retiro",
      messages: "Mensajes", profile: "Perfil", overview: "Resumen",
      committed: "Total comprometido", currentValue: "Valor actual",
      unrealized: "No realizado", allocation: "Asignación",
      performance: "Rendimiento de la cartera",
      kycPending: "Verificación pendiente", kycReview: "En revisión",
      kycApproved: "Verificado", kycRejected: "Verificación rechazada",
      kycStart: "Iniciar verificación",
      newWithdrawal: "Nueva solicitud de retiro",
      amount: "Monto", currency: "Divisa", bank: "Nombre del banco",
      iban: "IBAN", reason: "Motivo (opcional)", submit: "Enviar solicitud",
      noWithdrawals: "Aún no hay solicitudes de retiro.",
      unread: "no leídos",
    },
    admin: {
      title: "Administración",
      overview: "Resumen", investors: "Inversores",
      projectsMgmt: "Proyectos", docsMgmt: "Documentos",
      audit: "Registros de auditoría", contactMsgs: "Mensajes de contacto",
      approve: "Aprobar", reject: "Rechazar", pending: "pendiente",
      kycReview: "En revisión",
    },
    ai: {
      title: "Atenea — Asistente IA",
      subtitle: "Pregunte sobre nuestra infraestructura, tecnología o proyectos.",
      placeholder: "Pregunte a Atenea…",
      hint: "Impulsado por Strategy Intelligence Solutions",
      requiresAuth: "Inicie sesión para chatear con Atenea.",
    },
    footer: {
      tagline: "Infraestructura institucional para la era de la IA.",
      hq: "Sede social · Reino Unido",
      legal: "Aviso legal", privacy: "Privacidad", cookies: "Cookies", terms: "Términos",
      copyright: "Strategy Intelligence Solutions LTD. Todos los derechos reservados.",
    },
  },
};
